// Static question templates for the invite builder. Pure data — no imports,
// no API calls — so applying a template is instant and works offline.
//
// `questions` is an exact-length tuple on purpose: the builder replaces its
// question list wholesale on apply, and MAX_QUESTIONS in InviteBuilder is 5.
// Widening this type to string[] would let a template silently violate that
// cap; if a template ever needs a different length, change the builder's
// apply path, not this tuple.

export interface InviteTemplate {
  id: string
  label: string
  title: string
  context?: string
  questions: readonly [string, string, string, string, string]
}

export const INVITE_TEMPLATES: readonly InviteTemplate[] = [
  {
    id: 'frontend-engineer',
    label: 'Frontend Engineer',
    title: 'Frontend Engineer Interview',
    context:
      'Role focused on building accessible, performant user interfaces in React. We care about component design, state management, and shipping features users can actually use.',
    questions: [
      'Walk me through a UI feature you built end to end — how did you decide on the component structure and state ownership?',
      'Tell me about a time you had to make a slow page feel fast. What did you measure first, and what actually fixed it?',
      'How do you decide between reaching for a library and writing it yourself? Give a concrete example.',
      'Describe a bug that only showed up in production. How did you reproduce and fix it?',
      'If you could change one thing about how your current or last team ships frontend work, what would it be and why?',
    ],
  },
  {
    id: 'backend-engineer',
    label: 'Backend Engineer',
    title: 'Backend Engineer Interview',
    context:
      'Role focused on building reliable APIs and data layers. We care about correctness under concurrency, clear contracts, and systems that fail loudly instead of silently.',
    questions: [
      'Walk me through a service you designed — what were the main failure modes, and how did the design handle them?',
      'Tell me about a production incident you were part of. What was the root cause, and what changed afterwards?',
      'How do you approach a database change that needs to ship without downtime?',
      'Describe a time you had to make an API simpler. What did you remove, and what broke or improved?',
      'What is a piece of code you are proud of that nobody else will ever see, and why does it matter?',
    ],
  },
  {
    id: 'product-manager',
    label: 'Product Manager',
    title: 'Product Manager Interview',
    context:
      'Role focused on owning a product area end to end: discovering what to build, deciding with imperfect information, and driving it to launch with a small team.',
    questions: [
      'Tell me about a product decision you made with incomplete data. How did you decide, and what would you do differently now?',
      'Walk me through a feature you shipped that flopped. What did you learn, and what did you change next?',
      'How do you prioritize when two stakeholders want opposite things? Give a real example.',
      'Describe how you figured out what users actually needed — not what they said they needed.',
      'What is the smallest change you shipped that had the biggest measurable impact?',
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    title: 'Sales Representative Interview',
    context:
      'Role focused on running a full sales cycle: finding and qualifying leads, running discovery conversations, and closing deals with a consultative approach.',
    questions: [
      'Walk me through your sales process from first touch to closed deal. Where do deals most often stall?',
      'Tell me about a deal you lost that taught you the most. What went wrong?',
      'How do you qualify out of a deal that will never close? What signals do you look for?',
      'Describe a time you had to rebuild trust with an unhappy prospect or customer.',
      'What does good discovery look like to you? Walk me through a real conversation you ran.',
    ],
  },
  {
    id: 'customer-support',
    label: 'Customer Support',
    title: 'Customer Support Specialist Interview',
    context:
      'Role focused on resolving customer issues across chat and email: diagnosing problems, communicating clearly with frustrated people, and feeding patterns back to the product team.',
    questions: [
      'Walk me through how you would handle an angry customer whose issue you cannot immediately fix.',
      'Tell me about a time you turned a frustrated customer into a satisfied one. What specifically did you do?',
      'How do you prioritize when five tickets arrive at once and they are all urgent?',
      'Describe a recurring problem you noticed and got fixed at the source instead of answering it forever.',
      'How do you explain a technical workaround to someone with no technical background?',
    ],
  },
  {
    id: 'general-behavioral',
    label: 'General Behavioral',
    title: 'General Behavioral Interview',
    context:
      'A role-agnostic set of behavioral questions covering ownership, collaboration, and how the candidate handles setbacks and change.',
    questions: [
      'Tell me about a project you owned end to end. What was hardest, and what did you deliver?',
      'Describe a conflict with a coworker. How did it start, and how did it end?',
      'Tell me about a failure you are willing to own publicly. What changed because of it?',
      'How do you decide what to work on when everything is labeled top priority?',
      'What kind of work makes you lose track of time, and how does that show up in your best projects?',
    ],
  },
]
